import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import PollIcon from "@mui/icons-material/Poll";
import CodeIcon from "@mui/icons-material/Code";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FormModal from "../../../components/FormModal/FormModal";
import Buttons from "../../../components/Buttons/Buttons";
import { GetSurveyById } from "../../../utils/apiCalls/surveyBuilder";
import { downloadJSON } from "../../../utils/downloadFile";
import { isAssignable, STATUS_CHIP_CONFIG, getSurveyStatus, QUESTION_TYPES } from "../../../utils/surveysConstants";
import { useToast } from "../../../utils/ToastContext";
import "./SurveyViewModal.scss";

const QUESTION_TYPE_LABELS = {
  [QUESTION_TYPES.SHORT_TEXT]: "Short Text",
  [QUESTION_TYPES.LONG_TEXT]: "Long Text Response",
  [QUESTION_TYPES.NUMERIC]: "Numeric Input",
  [QUESTION_TYPES.DROPDOWN]: "Dropdown Select",
  [QUESTION_TYPES.YES_NO]: "Yes / No",
  [QUESTION_TYPES.SINGLE_CHOICE]: "Single Choice (Radio)",
  [QUESTION_TYPES.MULTI_CHOICE]: "Multiple Choice (Checkbox)",
  [QUESTION_TYPES.RATING]: "Rating Scale",
  [QUESTION_TYPES.SECTION]: "Section Divider",
  [QUESTION_TYPES.INSTRUCTION]: "Instruction Text",
};

const renderQuestionPreview = (q) => {
  switch (q.type) {
    case QUESTION_TYPES.SECTION:
      return null;
    case QUESTION_TYPES.INSTRUCTION:
      return null;
    case QUESTION_TYPES.SHORT_TEXT:
      return (
        <div className="survey-view-q_input-preview">
          <span>Participant enters short text response...</span>
        </div>
      );
    case QUESTION_TYPES.LONG_TEXT:
      return (
        <div className="survey-view-q_input-preview multiline">
          <span>Participant enters detailed text comments...</span>
        </div>
      );
    case QUESTION_TYPES.NUMERIC:
      return (
        <div className="survey-view-q_input-preview number">
          <span>Enter number...</span>
        </div>
      );
    case QUESTION_TYPES.DROPDOWN:
      return (
        <div className="survey-view-q_options-list">
          {(q.options || []).map((opt, i) => (
            <div key={i} className="survey-view-q_option-chip">
              <span className="bullet">•</span> {opt}
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPES.YES_NO:
    case QUESTION_TYPES.SINGLE_CHOICE:
      return (
        <div className="survey-view-q_options-list">
          {(q.options || ["Yes", "No"]).map((opt, i) => (
            <div key={i} className="survey-view-q_option-chip radio">
              <span className="circle" /> {opt}
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPES.MULTI_CHOICE:
      return (
        <div className="survey-view-q_options-list">
          {(q.options || []).map((opt, i) => (
            <div key={i} className="survey-view-q_option-chip checkbox">
              <span className="square" /> {opt}
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPES.RATING: {
      const min = Number.isFinite(q.scaleMin) ? q.scaleMin : 1;
      const max = Number.isFinite(q.scaleMax) ? q.scaleMax : 5;
      const steps = [];
      for (let n = min; n <= max; n++) steps.push(n);
      return (
        <div className="survey-view-q_rating-scale">
          {steps.map((n) => (
            <div key={n} className="rating-pill">
              {n}
            </div>
          ))}
        </div>
      );
    }
    default:
      return null;
  }
};

const SurveyViewModal = ({ survey, onClose, onAssign }) => {
  const { showToast } = useToast();
  const [surveyData, setSurveyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    if (!survey?.id) return;
    setIsLoading(true);
    setError("");

    GetSurveyById(survey.id)
      .then((res) => {
        if (ignore) return;
        if (res.statusCode === 200 && res.data) {
          setSurveyData(res.data);
        } else {
          setError(res.message || "Failed to load survey details");
        }
      })
      .catch((err) => {
        if (!ignore) setError(err?.message || "Failed to load survey details");
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [survey]);

  const handleExportJSON = () => {
    if (surveyData) {
      downloadJSON(`${surveyData.title || survey.title || "survey"}-definition.json`, surveyData);
      showToast("Survey JSON exported", "success");
    } else {
      downloadJSON(`${survey.title || "survey"}-definition.json`, survey);
      showToast("Survey JSON exported", "success");
    }
  };

  const status = survey ? getSurveyStatus(survey) : "draft";
  const chipConfig = STATUS_CHIP_CONFIG[status] || { label: status, color: "default" };
  const questions = surveyData?.questions || [];

  return (
    <FormModal
      open
      onClose={onClose}
      maxWidth="md"
      title={null}
      actions={
        <div className="survey-view-modal_footer_actions">
          <Buttons
            text="Close"
            type="button"
            variant="secondary"
            onClick={onClose}
            style={{ width: "90px" }}
          />
          <Buttons
            text="Export JSON"
            type="button"
            variant="outline-warning"
            icon={<CodeIcon fontSize="small" />}
            onClick={handleExportJSON}
            style={{ width: "130px" }}
          />
          {isAssignable(survey) && onAssign && (
            <Buttons
              text="Assign Survey"
              type="button"
              variant="accent"
              icon={<PersonAddIcon fontSize="small" />}
              onClick={() => {
                onClose();
                onAssign(survey);
              }}
              style={{ width: "140px" }}
            />
          )}
        </div>
      }
    >
      <div className="survey-view-modal">
        {/* EXECUTIVE HEADER */}
        <div className="survey-view-modal_header">
          <div className="survey-view-modal_header_left">
            <div className="survey-view-modal_header_icon">
              <PollIcon />
            </div>
            <div>
              <div className="survey-view-modal_title_row">
                <h2>{surveyData?.title || survey?.title || "Survey Details"}</h2>
                <Chip label={chipConfig.label} color={chipConfig.color} size="small" />
              </div>
              <p className="survey-view-modal_subtitle">
                {surveyData?.studyName ? `${surveyData.studyName} • ` : ""}
                Version {surveyData?.version || survey?.version || "1"} • Read-Only Clinical View
              </p>
            </div>
          </div>
        </div>

        {/* METRICS STATS SUMMARY */}
        <div className="survey-view-modal_stats">
          <div className="survey-view-stat_card">
            <span className="survey-view-stat_label">Total Questions</span>
            <strong className="survey-view-stat_val">{questions.length} Items</strong>
          </div>
          <div className="survey-view-stat_card">
            <span className="survey-view-stat_label">Status</span>
            <strong className="survey-view-stat_val capitalize">{status}</strong>
          </div>
          <div className="survey-view-stat_card">
            <span className="survey-view-stat_label">Version</span>
            <strong className="survey-view-stat_val">v{surveyData?.version || survey?.version || "1.0"}</strong>
          </div>
        </div>

        {/* DESCRIPTION CONTAINER */}
        {(surveyData?.description || survey?.description) && (
          <div className="survey-view-modal_desc">
            <span className="desc-label">Survey Description & Clinical Objective</span>
            <p>{surveyData?.description || survey?.description}</p>
          </div>
        )}

        {/* MAIN BODY CONTENT */}
        <div className="survey-view-modal_body">
          {isLoading ? (
            <div className="survey-view-modal_loading">
              <CircularProgress size={36} style={{ color: "#104581" }} />
              <p>Loading survey questions...</p>
            </div>
          ) : error ? (
            <div className="survey-view-modal_error">
              <p>{error}</p>
            </div>
          ) : questions.length > 0 ? (
            <div className="survey-view-q_list">
              {questions.map((q, idx) => {
                if (q.type === QUESTION_TYPES.SECTION) {
                  return (
                    <div key={q.id || idx} className="survey-view-section-divider">
                      <span>{q.label || "Section"}</span>
                    </div>
                  );
                }
                if (q.type === QUESTION_TYPES.INSTRUCTION) {
                  return (
                    <div key={q.id || idx} className="survey-view-instruction-box">
                      <HelpOutlineIcon fontSize="small" />
                      <p>{q.label}</p>
                    </div>
                  );
                }

                // If label matches default Q1/Q2 code, format it nicely or hide duplicate header
                const isGenericQCode = q.label && /^Q\d+$/i.test(q.label.trim());
                const questionTitle = isGenericQCode ? "" : q.label;

                return (
                  <div key={q.id || idx} className="survey-view-q_card">
                    <div className="survey-view-q_card_top">
                      <div className="survey-view-q_badge_row">
                        <span className="q-num">Q{idx + 1}</span>
                        <span className="q-type">{QUESTION_TYPE_LABELS[q.type] || q.type}</span>
                        {q.required && <span className="q-req">* Required</span>}
                      </div>
                    </div>

                    {questionTitle ? (
                      <h4 className="survey-view-q_label">{questionTitle}</h4>
                    ) : null}
                    {q.helpText && <p className="survey-view-q_help">{q.helpText}</p>}

                    <div className="survey-view-q_body">{renderQuestionPreview(q)}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="survey-view-modal_empty">
              <p>No questions configured for this survey.</p>
            </div>
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default SurveyViewModal;
